import React from 'react';
import { useQuote } from '../../contexts/QuoteContext';
import { useQuestionnaire } from '../../contexts/QuestionnaireContext';
import QuestionnaireEngine from './QuestionnaireEngine';

/**
 * Adapter component for quote requests - independent of BookingContext
 * This component handles questionnaire submissions for quote requests only
 * Integrates with QuestionnaireContext for database saving and lead tracking
 */
const QuoteQuestionnaireAdapter = ({
  configPath = '/data/customer_engagement_questionnaire.json',
  onComplete,
  onError,
  ...questionnaireProps
}) => {
  const { submitQuoteRequest, isSubmitting, submitError } = useQuote();
  const { saveFormProgress, saveStepProgress } = useQuestionnaire();

  // Handle questionnaire updates - save to database after each question
  const handleQuestionnaireUpdate = async (answers, questionId, value) => {
    try {
      // Map the current answers to a format suitable for database storage
      const mappedData = mapAnswersForDatabase(answers);
      
      console.log('🔍 DEBUG: About to save quote questionnaire data:', {
        questionId,
        value,
        rawAnswers: answers,
        mappedData,
        totalAnswers: Object.keys(answers).length,
        mappedDataKeys: Object.keys(mappedData)
      });
      
      // Save progress to database (debounced save)
      await saveFormProgress('quote_questionnaire', mappedData);
      
      console.log('📋 Quote questionnaire progress saved to database:', {
        questionId,
        totalAnswers: Object.keys(answers).length
      });
      
    } catch (error) {
      console.error('❌ Error saving quote questionnaire progress:', error);
      // Don't throw error - continue questionnaire even if save fails
    }
  };

  // Handle questionnaire completion for quotes
  const handleQuestionnaireComplete = async (answers) => {
    try {
      // Save final completion state to database (immediate save)
      const mappedData = mapAnswersForDatabase(answers);
      await saveStepProgress('quote_questionnaire_complete', {
        ...mappedData,
        completedAt: new Date().toISOString(),
        status: 'completed'
      });
      
      // Extract customer information from answers
      const customerInfo = extractCustomerInfo(answers);
      
      // Extract quote-specific information  
      const quoteInfo = extractQuoteInfo(answers);
      
      // Submit quote request using the quote context
      const result = await submitQuoteRequest(customerInfo, quoteInfo);
      
      if (result.success) {
        console.log('✅ Quote request submitted successfully and saved to database');
        onComplete?.(customerInfo, quoteInfo, answers);
      } else {
        throw new Error(result.error || 'Failed to submit quote request');
      }
      
    } catch (error) {
      console.error('❌ Error completing quote questionnaire:', error);
      onError?.(error);
      throw error;
    }
  };

  // Handle questionnaire errors
  const handleQuestionnaireError = (error) => {
    console.error('Quote questionnaire error:', error);
    onError?.(error);
  };

  // Map questionnaire answers to database-friendly format
  const mapAnswersForDatabase = (answers) => {
    // Store all answers as-is for complete lead tracking
    const mappedData = { ...answers };
    
    // Add metadata for better lead tracking
    mappedData.questionnaire_type = 'quote_request';
    mappedData.source = 'get_quote_questionnaire';
    mappedData.timestamp = new Date().toISOString();
    
    // Extract key lead information for easier querying
    if (answers.name) {
      const nameParts = answers.name.split(' ');
      mappedData.firstName = nameParts[0] || '';
      mappedData.lastName = nameParts.slice(1).join(' ') || '';
    }
    
    // Normalize key fields
    if (answers.email) mappedData.email = answers.email.toLowerCase().trim();
    if (answers.phone) mappedData.phone = answers.phone.replace(/\D/g, ''); // Store digits only
    
    return mappedData;
  };

  // Extract customer information from questionnaire answers
  const extractCustomerInfo = (answers) => {
    const customerInfo = {};
    
    // Handle name splitting
    if (answers.name) {
      const nameParts = answers.name.split(' ');
      customerInfo.firstName = nameParts[0] || '';
      customerInfo.lastName = nameParts.slice(1).join(' ') || '';
      customerInfo.name = answers.name; // Keep full name too
    }
    
    if (answers.email) {
      customerInfo.email = answers.email;
    }
    
    if (answers.phone) {
      customerInfo.phone = answers.phone;
    }

    if (answers.address) {
      customerInfo.address = answers.address;
    }

    return customerInfo;
  };

  // Extract quote-specific information from questionnaire answers
  const extractQuoteInfo = (answers) => {
    const quoteInfo = {};
    
    // Property details
    if (answers.residenceType) {
      quoteInfo.residenceType = answers.residenceType;
    }
    
    if (answers.squareFootage) {
      quoteInfo.squareFootage = answers.squareFootage;
    }
    
    if (answers.bedrooms && answers.bedrooms !== 'Office') {
      quoteInfo.bedrooms = answers.bedrooms;
    }
    
    if (answers.bathrooms && answers.bathrooms !== 'Office') {
      quoteInfo.bathrooms = answers.bathrooms;
    }

    // Service details
    if (answers.frequency) {
      quoteInfo.frequency = answers.frequency;
    }
    
    if (answers.cleanByDate) {
      quoteInfo.cleanByDate = answers.cleanByDate;
    }

    if (answers.lastCleaned) {
      quoteInfo.lastCleaned = answers.lastCleaned;
    }
    
    if (answers.cleanlinessLevel) {
      quoteInfo.cleanlinessLevel = answers.cleanlinessLevel;
    }

    if (answers.address) {
      quoteInfo.address = answers.address;
    }
    
    if (answers.accessInstructions) {
      quoteInfo.accessInstructions = answers.accessInstructions;
    }
    
    if (answers.decisionMaker) {
      quoteInfo.decisionMaker = answers.decisionMaker;
    }

    if (answers.specialOccasion) {
      quoteInfo.specialOccasion = answers.specialOccasion;
    }

    return quoteInfo;
  };

  return (
    <QuestionnaireEngine
      configPath={configPath}
      onComplete={handleQuestionnaireComplete}
      onUpdate={handleQuestionnaireUpdate}
      onError={handleQuestionnaireError}
      autoSave={false} // Disabled - using database saving instead
      showProgress={true}
      allowNavigation={true}
      isSubmitting={isSubmitting}
      submitError={submitError}
      {...questionnaireProps}
    />
  );
};

export default QuoteQuestionnaireAdapter;